import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiShoppingBag, 
  FiTruck, 
  FiBriefcase, 
  FiPackage, 
  FiGrid,
  FiCheck,
  FiArrowRight
} from 'react-icons/fi';
import '../../styles/onboarding.css';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BusinessCategorySelector = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const businessCategories = [
    {
      id: 'Retailer',
      title: 'Retailer',
      description: 'Sell products directly to end consumers',
      icon: FiShoppingBag,
      color: '#3498db'
    },
    {
      id: 'Distributor/Stockist/Wholesaler',
      title: 'Distributor/Stockist/Wholesaler',
      description: 'Distribute products to retailers and businesses',
      icon: FiTruck,
      color: '#e74c3c'
    },
    {
      id: 'Service Sector',
      title: 'Service Sector',
      description: 'Provide services to clients and customers',
      icon: FiBriefcase,
      color: '#f39c12'
    },
    {
      id: 'FMCG',
      title: 'FMCG',
      description: 'Fast Moving Consumer Goods business',
      icon: FiPackage,
      color: '#9b59b6'
    },
    {
      id: 'Others',
      title: 'Others',
      description: 'Other business categories not listed above',
      icon: FiGrid,
      color: '#95a5a6'
    }
  ];

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setError(''); // Clear any previous errors
  };

  const handleContinue = async () => {
    if (!selectedCategory) return;

    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Use the correct API endpoint from the backend
      const response = await fetch(`${API_BASE_URL}/api/accounts/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          business_category: selectedCategory
        })
      });
      
      if (response.ok) {
        // Redirect to dashboard after successful onboarding
        navigate('/dashboard');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to complete onboarding');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      setError(error.message || 'Failed to complete onboarding. Please try again.');
      
      // If it's an authentication error, redirect to login
      if (error.message.includes('token') || error.message.includes('login')) {
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <div className="brand-logo">
            <h2>Hamara Bill</h2>
            <p className="brand-subtitle">By TFS Traders</p>
          </div>
          <h1>Welcome to Hamara Bill!</h1>
          <p className="text-muted">
            To get started, please select your business category. This helps us customize your experience.
          </p>
        </div>

        <div className="category-grid">
          {businessCategories.map((category) => {
            const IconComponent = category.icon;
            const isSelected = selectedCategory === category.id;
            
            return (
              <div
                key={category.id}
                className={`category-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleCategorySelect(category.id)}
              >
                <div className="category-icon" style={{ backgroundColor: category.color }}>
                  <IconComponent />
                </div>
                <div className="category-content">
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                </div>
                {isSelected && (
                  <div className="selection-indicator">
                    <FiCheck />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="error-message" style={{ 
            color: '#e74c3c', 
            textAlign: 'center', 
            marginBottom: '1rem',
            padding: '0.5rem',
            backgroundColor: '#fdf2f2',
            borderRadius: '4px',
            border: '1px solid #fecaca'
          }}>
            {error}
          </div>
        )}

        <div className="onboarding-actions">
          <button
            className={`btn btn-primary btn-block ${!selectedCategory || isLoading ? 'disabled' : ''}`}
            onClick={handleContinue}
            disabled={!selectedCategory || isLoading}
          >
            {isLoading ? (
              'Setting up your account...'
            ) : (
              <>
                Continue to Dashboard
                <FiArrowRight />
              </>
            )}
          </button>
        </div>

        <div className="onboarding-footer">
          <p className="text-muted">
            You can change this later in your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessCategorySelector;